// IMPORTS
import { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";

// COMPONENTS
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import ErrorBox from "../../components/ErrorBox";
import BackToDashboard from "../../components/BackToDashboard";
import ErrorMessage from "../../components/ErrorMessage";
import RoundedTable, {
  TABLE_CELL_TYPES,
} from "../../components/table-component/RoundedTable";
import PageHeadline from "../../components/PageHeadline";
import AreYouSureModal from "../../components/AreYouSureModal";
import InformationBox from "../../components/InformationBox";
import Modal from "../../components/Modal";

// UTILS & SERVICES
import useLanguage from "../../utils/useLanguage";
import { UserAuth } from "../../utils/AuthContext";
import { DataContext } from "../../utils/DataContext";
import { formatDate } from "../../utils/toDate";
import {
  getAllKassas,
  updateKassa,
  deleteKassa,
} from "../../services/kassa/kassaServices";

// ASSETS
import nextIcon from "../../assets/next_icon.png";
import previousIcon from "../../assets/previous_icon.png";
import addIcon from "../../assets/add_icon_light.png";
import searchIcon from "../../assets/search_icon.png";
import editIcon from "../../assets/edit_icon.png";
import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";
import AddKassaProcess from "../../components/AddKassa";
import PageWrapper from "../../components/PageWrapper";

export default function KassatPage() {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const { userData } = useContext(UserAuth);
  const { loading, setLoading } = useContext(DataContext);

  const [kassas, setKassas] = useState([]);
  const [selectedKassa, setSelectedKassa] = useState(null);
  const [selectedKassas, setSelectedKassas] = useState([]);

  const [showEditKassaModal, setShowEditKassaModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });
  const [loadingModal, setLoadingModal] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const getKassas = async () => {
    try {
      setLoading(true);
      const response = await getAllKassas({
        page: pagination.currentPage,
        limit: 5,
        search: searchTerm,
      });

      if (response.data?.kassas) {
        setKassas(response.data.kassas);
        setPagination((prev) => ({
          ...prev,
          totalPages: response.data.totalPages,
          totalRecords: response.data.totalRecords,
        }));
      }
    } catch (error) {
      console.error("Failed to get the Kassas", error);
      setError(true);
      setErrorMessage(t("errorGettingKassat"));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteKassa = async (kassaId) => {
    try {
      setLoadingModal(true);
      const response = await deleteKassa(kassaId);

      if (response.status === 200) {
        await getKassas();
        setPagination((prev) => ({
          ...prev,
          totalRecords: prev.totalRecords - 1,
        }));
      }
    } catch (error) {
      console.error("Error deleting kassa:", error);
      setError(true);
      setErrorMessage("Failed to delete kassa");
    } finally {
      setShowDeleteConfirmation(false);
      setLoadingModal(false);
    }
  };

  const openEditKassaModal = (kassa) => {
    setSelectedKassa(kassa);
    setShowEditKassaModal(true);
  };

  const handleSearch = () => {
    getKassas();
  };

  const calculateBalance = (kassaTransactions) => {
    if (!kassaTransactions || kassaTransactions.length === 0) {
      return 0;
    }

    return kassaTransactions.reduce((total, transaction) => {
      return total + (transaction.amount || 0);
    }, 0);
  };

  const formatBalance = (balance) => {
    const isPositive = balance >= 0;
    return (
      <span style={{ color: isPositive ? "green" : "red", fontWeight: "bold" }}>
        {isPositive}
        {balance}
      </span>
    );
  };

  const titles = [
    { value: "kassaName" },
    { value: "balance" },
    { value: "creationDate" },
    { value: "info", keepOnMobile: true },
  ];

  const rows = kassas?.map((kassa) => [
    {
      type: TABLE_CELL_TYPES.LINK,
      value: kassa.name,
      keepOnMobile: true,
      to: `/kassa-page/${kassa.id}`,
    },
    {
      type: TABLE_CELL_TYPES.ELEMENT,
      value: formatBalance(kassa.balance),
    },
    {
      type: TABLE_CELL_TYPES.TEXT,
      value: formatDate(new Date()),
    },
    {
      value: (
        <CustomButton
          variant="blank"
          icon={editIcon}
          iconSize={19}
          title={t("edit")}
          onClick={() => openEditKassaModal(kassa)}
        />
      ),
      type: TABLE_CELL_TYPES.ELEMENT,
      keepOnMobile: true,
    },
  ]);

  useEffect(() => {
    getKassas();
  }, [pagination.currentPage, userData]);

  return (
    <PageWrapper title={t("kassat")} withBackButton>
      <PageWrapper
          style={{ direction: currentDirection }}
          title={t("organizations")}
          withBackButton
      >
        <div className="o-row o-row--horizontal-align_space-between u-margin--top_700 u-margin--bottom_700">
          {userData?.userId && (
            <div className="o-row o-row--gap-size_300">
              <AddKassaProcess
                icon={{ link: addIcon, size: 20, variant: "primary" }}
                onKassaCreated={() => getKassas()}
              />
            </div>
          )}
        </div>

        <section className="c-search-bar">
          <section className="c-search-bar-label-input">
            <CustomInput
              placeholder={`${t("searchTitle")} ${t("kassaName")}`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              resetSearch={() => {
                const searchValue = "";
                setSearchTerm(searchValue);
                getKassas();
              }}
              thick
              flex
            />
          </section>
          <CustomButton
            title={t("search")}
            onClick={handleSearch}
            icon={searchIcon}
            iconSize={15}
            classList="c-search-button"
          />
        </section>

        {loading && <Loader size={LOADER_SIZES.MEDIUM} />}

        {error && <ErrorBox>{errorMessage}</ErrorBox>}

        {!loading && !error && kassas.length === 0 && (
          <InformationBox>
            <p>{t("noKassasFound")}</p>
          </InformationBox>
        )}

        {!loading && !error && kassas.length > 0 && (
          <ul className="users-list">
            {error && <ErrorMessage>{errorMessage}</ErrorMessage>}
            <RoundedTable
              titles={titles}
              rows={rows}
              fullData={kassas}
              pagination={pagination}
              setPagination={setPagination}
              selectedRows={selectedKassas}
              setSelectedRows={setSelectedKassas}
              mobileEffect
            />
          </ul>
        )}

        {showEditKassaModal && (
          <EditKassaModal
            onClose={() => setShowEditKassaModal(false)}
            selectedKassa={selectedKassa}
            onKassaUpdated={() => {
              setShowEditKassaModal(false);
              getKassas();
            }}
          />
        )}

        {showDeleteConfirmation && (
          <AreYouSureModal
            onClose={() => setShowDeleteConfirmation(false)}
            onConfirm={() => handleDeleteKassa(selectedKassa.id)}
            customMessage={t("deleteKassa")}
            closeTitle="cancel"
            confirmTitle="confirm"
            loading={loadingModal}
            confirmIconType="whiteCheck"
            cancel
          />
        )}
      </PageWrapper>
    </PageWrapper>
  );
}

function EditKassaModal({ onClose, selectedKassa, onKassaUpdated }) {
  const { t } = useTranslation("general");
  const [loadingModal, setLoadingModal] = useState(false);
  const [editKassaFieldsModal, setEditKassaFieldsModal] = useState({
    name: selectedKassa.name,
  });
  const [updatingKassaError, setUpdatingKassaError] = useState();

  const handleUpdateKassa = async () => {
    if (!editKassaFieldsModal.name.trim()) {
      setUpdatingKassaError("noKassaName");
      return;
    }
    setLoadingModal(true);
    try {
      await updateKassa(selectedKassa.id, {
        name: editKassaFieldsModal.name.trim(),
      });
      onKassaUpdated();
    } catch (error) {
      console.error("Failed to update the Kassa", error);
      setUpdatingKassaError("errorUpdatingKassa");
    } finally {
      setLoadingModal(false);
    }
  };

  return (
    <Modal
      title={t("editKassa")}
      onClose={onClose}
      error={t(updatingKassaError)}
      footer={
        <>
          {loadingModal ? <Loader size={LOADER_SIZES.MEDIUM} /> : null}
          <section className="edit-user-actions">
            <CustomButton
              title={t("confirm")}
              onClick={handleUpdateKassa}
              icon={saveIcon}
              iconSize={16}
              disabled={loadingModal}
            />
            <CustomButton
              title={t("cancel")}
              onClick={onClose}
              variant="blank"
              icon={closeIcon}
              iconSize={14}
            />
          </section>
        </>
      }
    >
      <form>
        <div>
          {updatingKassaError && <ErrorBox>{t(updatingKassaError)}</ErrorBox>}
          <div className="edit-user-entity">
            <CustomInput
              name="kassaName"
              value={editKassaFieldsModal.name}
              onChange={(e) =>
                setEditKassaFieldsModal((prev) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
              errorText={
                updatingKassaError === "noKassaName" && t("noKassaName")
              }
              title={t("kassaName")}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
}
